import BaseProxy from './Base';

class ProjectsProxy extends BaseProxy {
  constructor(parameters = {}) {
    super('jobs', parameters, 'api/v1');
  }

  getBallData(job_id, frame) {
    this.endpoint = `${this.endpoint}/${job_id}/${frame}/ball_data`;
    return this.load();
  }

  getCanonicalPoints({ job_id, ...params }) {
    this.setParameters(params);
    this.endpoint = `${this.endpoint}/${job_id}/tracks/canonical_points`;
    return this.load();
  }

  // EVENTS START

  addDefaultEvents(jobId) {
    return this.submit('post', `/${this.endpoint}/${jobId}/default_events`);
  }

  createEvent({ job_id, ...params }) {
    this.endpoint = `${this.endpoint}/${job_id}/events`;
    return this.submit('put', `/${this.endpoint}`, params);
  }

  deleteEvent({ job_id, events }) {
    this.endpoint = `${this.endpoint}/${job_id}/events`;
    return this.submit('delete', `/${this.endpoint}`, events);
  }

  getEvents({ job_id, ...params }) {
    this.setParameters(params);
    this.endpoint = `${this.endpoint}/${job_id}/events`;
    return this.load();
  }

  getEventsForDownload(job_id) {
    this.endpoint = `${this.endpoint}/${job_id}/events/downloads`;
    return this.submit('get', `/${this.endpoint}`, null, true);
  }

  getEventTypes() {
    this.endpoint = this.endpoint.replace('jobs', 'dict/event_types');
    return this.load();
  }

  updateEvent({ events, job_id }) {
    this.endpoint = `${this.endpoint}/${job_id}/events`;
    return this.submit('patch', `/${this.endpoint}`, events);
  }

  // EVENTS END

  getFieldPoints({ frame_id, job_id }) {
    this.endpoint = `${this.endpoint}/${job_id}/field_points/${frame_id}`;
    return this.load();
  }

  getFieldPointsCanonical({ job_id }) {
    this.endpoint = `${this.endpoint}/${job_id}/field`;
    return this.load();
  }

  getProjects(query) {
    const parameters = {};
    if (!Object.prototype.hasOwnProperty.call(query, 'page')) {
      parameters.page = 1;
    } else {
      parameters.page = query.page;
    }
    if (!Object.prototype.hasOwnProperty.call(query, 'limit')) {
      parameters.limit = 30;
    } else {
      parameters.limit = query.limit;
    }
    if (Object.prototype.hasOwnProperty.call(query, 'column')) {
      parameters.order = `${query.order === 'descending' ? '-' : ''}${query.column}`;
    } else {
      parameters.order = '-created_at';
    }
    if (Object.prototype.hasOwnProperty.call(query, 'filter')) {
      parameters.q = query.filter;
    }
    this.setParameters(parameters);
    return this.load();
  }

  getStatisticFrame(jobId, frame) {
    return this.submit('get', `/${this.endpoint}/${jobId}/frame_stats/${frame}`);
  }

  getStatisticGame(jobId) {
    return this.submit('get', `/${this.endpoint}/${jobId}/game_stats`);
  }

  getStatisticPlayers(jobId) {
    return this.submit('get', `/${this.endpoint}/${jobId}/players_stats`);
  }

  deleteLogs({ job_id, ids }) {
    this.endpoint = `${this.endpoint}/task_statuses/${job_id}`;
    return this.submit('delete', `/${this.endpoint}`, { jobs_ids: ids });
  }

  getTaskStatuses(params) {
    this.setParameters(params);
    this.endpoint = `${this.endpoint}/task_statuses`;
    return this.load();
  }

  getTaskStatusesForProject(jobId) {
    this.endpoint = `${this.endpoint}/task_statuses/${jobId}`;
    return this.load();
  }

  markAsFinishedTracks({ job_id, tracks }) {
    this.endpoint = `${this.endpoint}/${job_id}/tracks_mapping/review_status`;
    return this.submit('put', `/${this.endpoint}`, tracks);
  }

  search(keyword) {
    this.setParameter('q', keyword);
    return this.load();
  }

  create(options) {
    return this.submit('put', `/${this.endpoint}`, options);
  }

  // run({ id: jobId, formData }) {
  //   this.endpoint = `${this.endpoint}/${jobId}`;
  //   return this.create(formData);
  // }

  delete(jobsIds) {
    const ids = Array.isArray(jobsIds) ? jobsIds : [jobsIds];
    return this.submit('delete', `/${this.endpoint}`, { jobs_ids: ids });
  }

  getProgress(jobId) {
    this.endpoint = `${this.endpoint}/${jobId}/progress`;
    return this.load();
  }

  getProgressForJobs(params) {
    this.setParameters(params);
    this.endpoint = `${this.endpoint}/progress`;
    return this.load();
  }

  runJob(jobId, options) {
    this.endpoint = `${this.endpoint}/${jobId}/run`;
    return this.submit('put', `/${this.endpoint}`, options);
  }

  getTracksMapping(jobId) {
    this.endpoint = `${this.endpoint}/${jobId}/tracks_mapping`;
    return this.load();
  }

  deleteMapping(options) {
    this.endpoint = `${this.endpoint}/${options.job_id}/tracks_mapping/${options.track_id}`;
    return this.submit('delete', `/${this.endpoint}`);
  }

  deleteMappings(options) {
    const ids = Array.isArray(options.tracks_ids) ? options.tracks_ids : [options.track_id];
    this.endpoint = `${this.endpoint}/${options.job_id}/tracks_mapping`;
    return this.submit('delete', `/${this.endpoint}`, { tracks_ids: ids });
  }

  joinTracks(options) {
    const ids = Array.isArray(options.tracks_ids) ? options.tracks_ids : [options.track_id];
    this.endpoint = `${this.endpoint}/${options.job_id}/tracks_mapping/join`;
    return this.submit('put', `/${this.endpoint}`, { tracks_ids: ids });
  }

  splitTrack({ job_id, track_id, ...options }) {
    this.endpoint = `${this.endpoint}/${job_id}/tracks_mapping/${track_id}/split`;
    return this.submit('put', `/${this.endpoint}`, options);
  }

  splitTrackByJersey(job_id, track_id) {
    this.endpoint = `${this.endpoint}/${job_id}/split_track_by_jersey/${track_id}`;
    return this.submit('post', `/${this.endpoint}`);
  }

  swapTrack(options) {
    const { job_id, ...params } = options;
    this.endpoint = `${this.endpoint}/${job_id}/tracks_mapping/swap`;
    return this.submit('post', `/${this.endpoint}`, params);
  }

  updateMapping(options) {
    this.endpoint = `${this.endpoint}/${options.job_id}/tracks_mapping/${options.track_id}`;
    return this.submit('put', `/${this.endpoint}`, options.player);
  }

  generateTracksVideo({ job_id, type }) {
    this.endpoint = `${this.endpoint}/${job_id}/video/${type}`;
    return this.submit('put', `/${this.endpoint}`);
  }

  changeDetect(options) {
    const { job_id, ...params} = options;
    const data = { boxes: [params] };
    this.endpoint = `${this.endpoint}/${job_id}/boxes/detects`;
    return this.submit('put', `/${this.endpoint}`, data);
  }

  deleteDetect(job_id, data) {
    this.endpoint = `${this.endpoint}/${job_id}/boxes/detects`;
    return this.submit('delete', `/${this.endpoint}`, data);
  }

  getObjects(options) {
    this.endpoint = `${this.endpoint}/${options.job_id}/boxes/detects`;
    const parameters = {
      frames_count: 1,
      frame_id: options.frame_id !== undefined ? options.frame_id : '',
    };
    this.setParameters(parameters);
    return this.load();
  }

  refreshJerseys({ jobId, options }) {
    return this.submit('put', `/${this.endpoint}/${jobId}/jerseys`, options);
  }

  refreshTracks(job_id) {
    this.endpoint = `${this.endpoint}/${job_id}/tracks_mapping/refresh`;
    return this.submit('put', `/${this.endpoint}`);
  }

  runBallPossession(jobId) {
    return this.submit('put', `/${this.endpoint}/${jobId}/ball_possession`);
  }

  runEvents(jobId) {
    return this.submit('put', `/${this.endpoint}/${jobId}/run_job_game_events`);
  }

  runPostTracking(jobId) {
    return this.submit('put', `/${this.endpoint}/${jobId}/tasks/posttracking`);
  }

  runRebuildPostTracking(jobId) {
    return this.submit('put', `/${this.endpoint}/${jobId}/tasks/rebuild_posttracking`);
  }

  runRebuildPostTrackingTeam(jobId, teamId) {
    return this.submit('put', `/${this.endpoint}/${jobId}/tasks/rebuild_posttracking/team/${teamId}`);
  }

  runRebuildPostTrackingPlayer({job_id, ...params}) {
    return this.submit('put', `/${this.endpoint}/${job_id}/tasks/rebuild_posttracking/player`, params);
  }

  runZCoordinateForBall(jobId) {
    return this.submit('put', `/${this.endpoint}/${jobId}/ball_z`);
  }

  runInterpolationForJob(jobId) {
    return this.submit('put', `/${this.endpoint}/${jobId}/interpolation`);
  }

  runInterpolationForTracks({ job_id, ...params }) {
    return this.submit('post', `/${this.endpoint}/${job_id}/interpolation`, params);
  }

  runStatistic(jobId) {
    return this.submit('put', `/${this.endpoint}/${jobId}/game_stats`);
  }

  trimTrack({ job_id, track_id, ...params }) {
    this.endpoint = `${this.endpoint}/${job_id}/trim_track/${track_id}`;
    this.submit('post', `/${this.endpoint}`, params);
  }
}

export default ProjectsProxy;
